import { StateCreator } from "zustand";
import { 
  EphemeralWsRequest, 
  EphemeralWsRequestOptions, 
  startEphemeralWsRequest,
  EphemeralRequestState
} from "@sharpana/shared/utils/EphemeralWsRequest";

interface EphemeralWsSlice {
  // Active requests by ID
  ephemeralRequests: Map<string, EphemeralWsRequest<any, any>>;
  
  // Start a new ephemeral WebSocket request
  startEphemeralRequest: <TIn, TOut>(
    opts: EphemeralWsRequestOptions<TIn>
  ) => EphemeralWsRequest<TIn, TOut>;
  
  // Cancel a request by ID
  cancelEphemeralRequest: (requestId: string) => void;
  
  // Get last message for a specific request
  getLastMessage: (requestId: string) => any;
  
  // Get request state
  getRequestState: (requestId: string) => EphemeralRequestState;
  
  // Get all messages for a request
  getRequestMessages: (requestId: string) => any[];
  
  // Internal: cleanup finished requests
  cleanupFinishedRequests: () => void;
}

export const createEphemeralWsSlice: StateCreator<
  EphemeralWsSlice,
  [],
  [],
  EphemeralWsSlice
> = (set, get) => ({
  ephemeralRequests: new Map(),

  startEphemeralRequest: <TIn, TOut>(opts: EphemeralWsRequestOptions<TIn>) => {
    const request = startEphemeralWsRequest<TIn, TOut>(opts);
    
    // Subscribe to request changes to trigger store updates
    request.subscribe(() => {
      // Force store update when request changes
      set((state) => ({
        ephemeralRequests: (() => { const m = new Map(state.ephemeralRequests); m.set(request.id, request); return m; })()
      }));
      
      // Auto-cleanup when finished
      if (request.state === "finished" || request.state === "canceled" || request.state === "error") {
        setTimeout(() => {
          get().cleanupFinishedRequests();
        }, 1000); // Small delay to allow components to react to final state
      }
    });
    
    set((state) => ({
      ephemeralRequests: (() => { const m = new Map(state.ephemeralRequests); m.set(request.id, request); return m; })()
    }));
    
    return request;
  },

  cancelEphemeralRequest: (requestId: string) => {
    const { ephemeralRequests } = get();
    const request = ephemeralRequests.get(requestId);
    if (request) {
      request.cancel();
      // State update will happen via subscription
    }
  },

  getLastMessage: (requestId: string) => {
    const { ephemeralRequests } = get();
    const request = ephemeralRequests.get(requestId);
    return request?.lastMessage ?? null;
  },

  getRequestState: (requestId: string) => {
    const { ephemeralRequests } = get();
    const request = ephemeralRequests.get(requestId);
    return request?.state ?? "finished";
  },

  getRequestMessages: (requestId: string) => {
    const { ephemeralRequests } = get();
    const request = ephemeralRequests.get(requestId);
    return request?.messages ?? [];
  },

  cleanupFinishedRequests: () => {
    const { ephemeralRequests } = get();
    const newMap = new Map();
    
    ephemeralRequests.forEach((request, id) => {
      if (request.state === "connecting" || request.state === "streaming") {
        newMap.set(id, request);
      }
      // Remove finished/canceled/error requests
    });
    
    set({ ephemeralRequests: newMap });
  },
});
